import '../../Styles/App.css';
import '../../Styles/Admin.css';
import { useCallback, useEffect, useRef, useState } from 'react';
import { MdChevronLeft, MdChevronRight, MdExpandMore, MdPlayArrow, MdPause } from 'react-icons/md';
import { BiSolidPlaylist } from 'react-icons/bi';
import izenuAPI from '../../services/izenuAPI';

const BUCKETS = [
    { key: 'pending', label: 'Por verificar' },
    { key: 'verified', label: 'Verificadas' },
    { key: 'rejected', label: 'Rejeitadas' },
    { key: 'all', label: 'Todas' },
];

const PAGE_SIZE = 20;

function Badge({ children, color }) {
    return (
        <span style={{
            padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700,
            color: '#fff', backgroundColor: color, textTransform: 'uppercase',
            letterSpacing: 0.3, whiteSpace: 'nowrap',
        }}>{children}</span>
    );
}

function Cover({ url, title }) {
    const [err, setErr] = useState(false);
    if (!url || err) {
        return (
            <div style={{
                width: 72, height: 72, borderRadius: 8, flexShrink: 0,
                backgroundColor: '#eee', display: 'flex',
                alignItems: 'center', justifyContent: 'center',
            }}>
                <BiSolidPlaylist size={28} color="#999" />
            </div>
        );
    }
    return (
        <img
            src={url}
            alt={title}
            onError={() => setErr(true)}
            style={{ width: 72, height: 72, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }}
        />
    );
}

function formatDuration(value) {
    if (!value) return '';
    // music.duration is stored in milliseconds; a few old rows hold seconds.
    const total = Math.round(value > 10000 ? value / 1000 : value);
    return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

const STATE_LABELS = {
    unreviewed: { label: 'Por aprovar', color: '#f5a623' },
    pending: { label: 'Por aprovar', color: '#f5a623' },
    rejected: { label: 'Recusada', color: '#d32f2f' },
};

function PlaylistTracks({ playlistId, playingUrl, onToggle }) {
    const [tracks, setTracks] = useState(null);
    const [error, setError] = useState('');

    useEffect(() => {
        let cancelled = false;
        izenuAPI.get('/admin/playlists/tracks.php', { params: { playlist_id: playlistId } })
            .then(({ data: r }) => { if (!cancelled) setTracks(r.data || []); })
            .catch(err => { if (!cancelled) setError(err?.response?.data?.message || err.message); });
        return () => { cancelled = true; };
    }, [playlistId]);

    if (error) return <p style={{ margin: 0, fontSize: 13, color: '#d32f2f' }}>Erro ao carregar as faixas: {error}</p>;
    if (tracks === null) return <p style={{ margin: 0, fontSize: 13, color: '#888' }}>A carregar faixas…</p>;
    if (tracks.length === 0) return <p style={{ margin: 0, fontSize: 13, color: '#888' }}>Esta playlist não tem faixas.</p>;

    return (
        <ol style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {tracks.map((t, i) => {
                const playing = t.music_url && playingUrl === t.music_url;
                const state = STATE_LABELS[t.music_state];
                return (
                    <li key={t.row_id} style={{
                        display: 'flex', alignItems: 'center', gap: 12, padding: '6px 8px', borderRadius: 8,
                        backgroundColor: playing ? '#e3f2fd' : 'transparent',
                    }}>
                        <span style={{ width: 20, fontSize: 12, color: '#999', textAlign: 'right', flexShrink: 0 }}>{i + 1}</span>
                        <button
                            type="button"
                            onClick={() => onToggle(t.music_url)}
                            disabled={!t.music_url}
                            aria-label={playing ? `Pausar ${t.title}` : `Ouvir ${t.title}`}
                            style={{
                                width: 32, height: 32, borderRadius: '50%', border: 'none', flexShrink: 0,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                backgroundColor: t.music_url ? '#1976d2' : '#ddd', color: '#fff',
                                cursor: t.music_url ? 'pointer' : 'default',
                            }}
                        >{playing ? <MdPause size={18} /> : <MdPlayArrow size={18} />}</button>
                        {t.cover_url
                            ? <img src={t.cover_url} alt="" loading="lazy" style={{ width: 36, height: 36, borderRadius: 4, objectFit: 'cover', flexShrink: 0 }} />
                            : <div style={{ width: 36, height: 36, borderRadius: 4, backgroundColor: '#eee', flexShrink: 0 }} />}
                        <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                            <div style={{ fontSize: 14, color: '#222', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.title}</div>
                            <div style={{ fontSize: 12, color: '#777', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.singer}</div>
                        </div>
                        {state && <Badge color={state.color}>{state.label}</Badge>}
                        {t.exclusive && <Badge color="#9e9e9e">Exclusiva</Badge>}
                        <span style={{ fontSize: 12, color: '#999', flexShrink: 0, minWidth: 36, textAlign: 'right' }}>{formatDuration(t.duration)}</span>
                    </li>
                );
            })}
        </ol>
    );
}

export default function PlaylistReview() {
    const [bucket, setBucket] = useState('pending');
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [counts, setCounts] = useState({ pending: 0, verified: 0, rejected: 0 });
    const [q, setQ] = useState('');
    const [appliedQ, setAppliedQ] = useState('');
    const [minTracks, setMinTracks] = useState(1);
    const [loading, setLoading] = useState(false);
    const [busyId, setBusyId] = useState(null);
    const [expandedId, setExpandedId] = useState(null);
    const [playingUrl, setPlayingUrl] = useState(null);
    const audioRef = useRef(null);

    // One player for the whole page, so starting a track stops whatever was playing.
    useEffect(() => {
        const audio = new Audio();
        audio.onended = () => setPlayingUrl(null);
        audio.onerror = () => setPlayingUrl(null);
        audioRef.current = audio;
        return () => { audio.pause(); audio.src = ''; };
    }, []);

    const stopAudio = useCallback(() => {
        audioRef.current?.pause();
        setPlayingUrl(null);
    }, []);

    const toggleTrack = useCallback((url) => {
        const audio = audioRef.current;
        if (!audio || !url) return;
        if (playingUrl === url) {
            stopAudio();
            return;
        }
        audio.src = url;
        audio.play().then(() => setPlayingUrl(url)).catch(() => {
            setPlayingUrl(null);
            alert('Não foi possível reproduzir esta faixa.');
        });
    }, [playingUrl, stopAudio]);

    const toggleExpanded = useCallback((id) => {
        stopAudio();
        setExpandedId(prev => (prev === id ? null : id));
    }, [stopAudio]);

    // A new page or filter shows other playlists; nothing from the old list stays open or playing.
    useEffect(() => { setExpandedId(null); stopAudio(); }, [bucket, appliedQ, minTracks, page, stopAudio]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data: r } = await izenuAPI.get('/admin/playlists/list.php', {
                params: {
                    bucket,
                    q: appliedQ,
                    min_tracks: minTracks,
                    limit: PAGE_SIZE,
                    offset: page * PAGE_SIZE,
                },
            });
            if (r.response) {
                setItems(r.data);
                setTotal(r.total);
                setCounts(r.counts);
            }
        } catch (err) {
            console.log(err);
            alert('Erro ao carregar: ' + (err?.response?.data?.message || err.message));
        }
        setLoading(false);
    }, [bucket, appliedQ, minTracks, page]);

    useEffect(() => { load(); }, [load]);

    // Any filter change invalidates the current page number.
    const changeFilter = useCallback((fn) => { setPage(0); fn(); }, []);

    // Izenu's own editorial account: its playlists are the platform's, not a listener's.
    const EDITORIAL_USER_ID = 110;

    const setStatus = useCallback(async (item, status) => {
        const toPublic = status === 'public';
        const owner = item.user_id === EDITORIAL_USER_ID ? '' : `\n\nAtenção: é uma playlist de ${item.author || 'um utilizador'}, não da Izenu.`;
        if (!window.confirm(
            toPublic
                ? `Tornar «${item.title}» pública? Passa a aparecer na pesquisa e na descoberta${item.verified === 1 ? '' : ' quando for verificada'}.${owner}`
                : `Tornar «${item.title}» privada? Sai da pesquisa, da descoberta e da home.${owner}`
        )) return;
        setBusyId(item.id);
        try {
            await izenuAPI.post('/admin/playlists/setStatus.php', { playlist_id: item.id, status });
            await load();
        } catch (err) {
            alert('Erro: ' + (err?.response?.data?.message || err.message));
        }
        setBusyId(null);
    }, [load]);

    const setVerified = useCallback(async (item, verified) => {
        let note = null;

        if (!verified) {
            note = window.prompt(
                item.is_curator_playlist
                    ? `"${item.title}" é playlist de curadoria de ${item.curator_name}. Indica o motivo (obrigatório):`
                    : `Motivo da rejeição de "${item.title}" (opcional):`
            );
            if (item.is_curator_playlist && !note) return;
            if (note === null && !item.is_curator_playlist) return;
        }

        setBusyId(item.id);
        const send = async (confirmCurator) => izenuAPI.post('/admin/playlists/setVerified.php', {
            playlist_id: item.id,
            verified: verified ? 1 : 0,
            note,
            confirm_curator: confirmCurator,
        });

        try {
            const { data: r } = await send(false);
            if (r.warning) alert(r.warning);
            await load();
        } catch (err) {
            const d = err?.response?.data;
            // The backend refuses to un-verify an active curator playlist without
            // an explicit acknowledgement of the revenue impact.
            if (d?.code === 'CURATOR_PLAYLIST') {
                if (window.confirm(d.message)) {
                    try {
                        const { data: r2 } = await send(true);
                        if (r2.warning) alert(r2.warning);
                        await load();
                    } catch (e2) {
                        alert('Erro: ' + (e2?.response?.data?.message || e2.message));
                    }
                }
            } else {
                alert('Erro: ' + (d?.message || err.message));
            }
        }
        setBusyId(null);
    }, [load]);

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const from = total === 0 ? 0 : page * PAGE_SIZE + 1;
    const to = Math.min((page + 1) * PAGE_SIZE, total);

    const chipStyle = (active) => ({
        padding: '8px 18px', borderRadius: 20, border: '1px solid #e0e0e0', cursor: 'pointer',
        fontSize: 14, fontWeight: 600,
        backgroundColor: active ? '#1976d2' : '#fff',
        color: active ? '#fff' : '#666',
    });

    const pageBtnStyle = (disabled) => ({
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: 34, height: 34, borderRadius: 8,
        border: '1px solid #e0e0e0', backgroundColor: '#fff',
        cursor: disabled ? 'default' : 'pointer',
        color: disabled ? '#ccc' : '#333',
    });

    return (
        <div className="sm:p-6 text-left">
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
                <h2 style={{ margin: 0, fontSize: 22, color: '#222' }}>Verificação de Playlists</h2>
                <span style={{ fontSize: 13, color: '#888' }}>
                    {counts.pending} por verificar · {counts.verified} verificadas · {counts.rejected} rejeitadas
                </span>
            </div>
            <p style={{ margin: '-12px 0 20px', maxWidth: 720, fontSize: 14, lineHeight: 1.5, color: '#555', textAlign: 'left' }}>
                Só as playlists verificadas aparecem na pesquisa e na descoberta da app e do site. Uma playlist privada
                continua visível apenas para o dono, mesmo depois de verificada. Nas playlists de curadoria, a comissão
                de 2% só começa a contar depois da verificação.
            </p>

            <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
                {BUCKETS.map(b => (
                    <button
                        key={b.key}
                        onClick={() => changeFilter(() => setBucket(b.key))}
                        style={chipStyle(bucket === b.key)}
                    >{b.label}</button>
                ))}

                <input
                    type="text"
                    value={q}
                    placeholder="Pesquisar…"
                    onChange={(e) => setQ(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') changeFilter(() => setAppliedQ(q)); }}
                    onBlur={() => { if (q !== appliedQ) changeFilter(() => setAppliedQ(q)); }}
                    style={{
                        padding: '8px 12px', borderRadius: 8, border: '1px solid #ccc',
                        backgroundColor: '#fff', color: '#333', fontSize: 14, width: 200,
                    }}
                />
                <label style={{ fontSize: 13, color: '#888', display: 'flex', alignItems: 'center', gap: 6 }}>
                    Mín. faixas
                    <input
                        type="number"
                        min={0}
                        value={minTracks}
                        onChange={(e) => changeFilter(() => setMinTracks(Number(e.target.value)))}
                        style={{
                            width: 64, padding: '7px 8px', borderRadius: 6, border: '1px solid #ccc',
                            backgroundColor: '#fff', color: '#333', fontSize: 13,
                        }}
                    />
                </label>
            </div>

            {loading && <p style={{ color: '#888' }}>A carregar…</p>}
            {!loading && items.length === 0 && (
                <p style={{ color: '#888' }}>Nada nesta lista.</p>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {items.map(item => (
                    <div key={item.id} style={{
                        backgroundColor: '#fff', borderRadius: 12,
                        border: `1px solid ${expandedId === item.id ? '#90caf9' : '#e0e0e0'}`,
                        overflow: 'hidden',
                    }}>
                    <div
                        role="button"
                        tabIndex={0}
                        aria-expanded={expandedId === item.id}
                        onClick={() => toggleExpanded(item.id)}
                        onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); toggleExpanded(item.id); } }}
                        className="flex flex-wrap sm:flex-nowrap gap-3 sm:gap-4 items-center p-3 sm:p-4 cursor-pointer text-left"
                    >
                        <Cover url={item.image_url} title={item.title} />

                        <div className="flex-1 min-w-0 basis-[calc(100%-88px)] sm:basis-auto">
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                <strong style={{ fontSize: 16, color: '#222' }}>{item.title}</strong>
                                {item.verified === 1
                                    ? <Badge color="#4CAF50">Verificada</Badge>
                                    : item.verified_at
                                        ? <Badge color="#d32f2f">Rejeitada</Badge>
                                        : <Badge color="#f5a623">Por verificar</Badge>}
                                {item.status === 'private' && <Badge color="#757575">Privada</Badge>}
                                {item.is_curator_playlist && <Badge color="#9333ea">Curadoria · 2%</Badge>}
                                {item.weekly_slot && <Badge color="#1976d2">Slot: {item.weekly_slot}</Badge>}
                            </div>
                            <div style={{ fontSize: 13, color: '#666', marginTop: 4, textAlign: 'left' }}>
                                {item.author} · {item.track_count} faixa{item.track_count !== 1 ? 's' : ''}
                                {item.play_count > 0 && ` · ${item.play_count} reproduç${item.play_count === 1 ? 'ão' : 'ões'}`}
                                {item.favorites_count > 0 && ` · ${item.favorites_count} guardadas`}
                                {!item.image_url && ' · sem capa'}
                                {item.description && ` · ${item.description}`}
                            </div>
                            {item.is_curator_playlist && item.verified !== 1 && (
                                <div style={{
                                    marginTop: 8, padding: '6px 10px', borderRadius: 6,
                                    backgroundColor: '#fff8e1', border: '1px solid #ffe082',
                                    fontSize: 12, color: '#b26a00',
                                }}>
                                    Curadoria aprovada mas não verificada — não aparece na descoberta e não gera ganhos.
                                </div>
                            )}
                            {item.status === 'private' && item.verified === 1 && (
                                <div style={{
                                    marginTop: 8, padding: '6px 10px', borderRadius: 6,
                                    backgroundColor: '#f5f5f5', border: '1px solid #e0e0e0',
                                    fontSize: 12, color: '#666',
                                }}>
                                    Privada — invisível na pesquisa, na descoberta e na home, mesmo verificada. Usa «Tornar pública» para a mostrar.
                                </div>
                            )}
                            {item.verification_note && (
                                <div style={{ marginTop: 6, fontSize: 12, color: '#888', fontStyle: 'italic' }}>
                                    Nota: {item.verification_note}
                                    {item.verified_by_name && ` — ${item.verified_by_name}`}
                                </div>
                            )}
                        </div>

                        <div className="flex gap-2 shrink-0 items-center w-full sm:w-auto justify-end" onClick={(e) => e.stopPropagation()}>
                            <button
                                onClick={() => setStatus(item, item.status === 'private' ? 'public' : 'private')}
                                disabled={busyId === item.id}
                                title={item.status === 'private' ? 'Privada — clica para tornar pública' : 'Pública — clica para tornar privada'}
                                style={{
                                    padding: '8px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                                    border: `1px solid ${item.status === 'private' ? '#2e7d32' : '#bbb'}`,
                                    backgroundColor: '#fff', color: item.status === 'private' ? '#2e7d32' : '#666',
                                    opacity: busyId === item.id ? 0.5 : 1,
                                }}
                            >{item.status === 'private' ? 'Tornar pública' : 'Tornar privada'}</button>
                            {item.verified !== 1 && (
                                <button
                                    onClick={() => setVerified(item, true)}
                                    disabled={busyId === item.id}
                                    style={{
                                        padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer',
                                        backgroundColor: '#4CAF50', color: '#fff', fontSize: 13, fontWeight: 600,
                                        opacity: busyId === item.id ? 0.5 : 1,
                                    }}
                                >Verificar</button>
                            )}
                            {(item.verified === 1 || !item.verified_at) && (
                                <button
                                    onClick={() => setVerified(item, false)}
                                    disabled={busyId === item.id}
                                    style={{
                                        padding: '8px 16px', borderRadius: 8,
                                        border: '1px solid #d32f2f', cursor: 'pointer',
                                        backgroundColor: '#fff', color: '#d32f2f',
                                        fontSize: 13, fontWeight: 600,
                                        opacity: busyId === item.id ? 0.5 : 1,
                                    }}
                                >{item.verified === 1 ? 'Remover' : 'Rejeitar'}</button>
                            )}
                            <MdExpandMore
                                size={24}
                                color="#999"
                                aria-hidden="true"
                                onClick={() => toggleExpanded(item.id)}
                                style={{ cursor: 'pointer', transition: 'transform .2s', transform: expandedId === item.id ? 'rotate(180deg)' : 'none' }}
                            />
                        </div>
                    </div>
                    {expandedId === item.id && (
                        <div className="px-2 sm:px-4 pt-3 pb-4" style={{ borderTop: '1px solid #eee', backgroundColor: '#fafafa' }}>
                            <PlaylistTracks playlistId={item.id} playingUrl={playingUrl} onToggle={toggleTrack} />
                        </div>
                    )}
                    </div>
                ))}
            </div>

            {total > 0 && (
                <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    gap: 12, marginTop: 20, flexWrap: 'wrap',
                }}>
                    <span style={{ fontSize: 13, color: '#888' }}>
                        {from}–{to} de {total}
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <button
                            onClick={() => setPage(p => Math.max(0, p - 1))}
                            disabled={page === 0}
                            style={pageBtnStyle(page === 0)}
                        ><MdChevronLeft size={20} /></button>

                        <span style={{ fontSize: 13, color: '#666', minWidth: 90, textAlign: 'center' }}>
                            Página {page + 1} de {totalPages}
                        </span>

                        <button
                            onClick={() => setPage(p => (p + 1 < totalPages ? p + 1 : p))}
                            disabled={page + 1 >= totalPages}
                            style={pageBtnStyle(page + 1 >= totalPages)}
                        ><MdChevronRight size={20} /></button>
                    </div>
                </div>
            )}
        </div>
    );
}
